import * as React from "react";
import { Stack } from "@mui/material";
import { StatusCard } from "@/components/dashboard/StatusCard";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { HealthBadge } from "@/components/dashboard/HealthBadge";
import { QueryStateBoundary } from "@/components/dashboard/QueryStateBoundary";
import { useReadiness, useLiveness } from "@/hooks/queries/useHealthStatus";

interface ReadinessPayload {
  status: string;
  lifecycle?: { total_components: number; ready_components: number; error_components: number } | null;
}

interface LivenessPayload {
  status: string;
}

/** Reuses Sprint 3.2's `useReadiness`/`useLiveness` hooks - same pattern as Dashboard's PlatformHealthSection. */
export function HealthSection(): React.JSX.Element {
  const readinessQuery = useReadiness();
  const livenessQuery = useLiveness();

  return (
    <StatusCard title="Health">
      <Stack spacing={1.5}>
        <QueryStateBoundary query={readinessQuery}>
          {(payload) => {
            const readiness = payload as unknown as ReadinessPayload;
            const lifecycle = readiness.lifecycle;
            const diagnostics = lifecycle
              ? `${lifecycle.ready_components}/${lifecycle.total_components} components ready, ${lifecycle.error_components} in error`
              : null;
            return (
              <>
                <MetricCard label="Overall Health" render={<HealthBadge status={readiness.status} />} />
                <MetricCard label="Readiness" render={<HealthBadge status={readiness.status} />} />
                <MetricCard label="Diagnostics" value={diagnostics} />
              </>
            );
          }}
        </QueryStateBoundary>
        <QueryStateBoundary query={livenessQuery}>
          {(payload) => (
            <MetricCard
              label="Liveness"
              render={<HealthBadge status={(payload as unknown as LivenessPayload).status} />}
            />
          )}
        </QueryStateBoundary>
      </Stack>
    </StatusCard>
  );
}
